// FileViewerSideBar.jsx
// Sidebar component for the FileViewer

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework Imports
import React from "react"; // { useState, createContext, useEffect }
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";

// HSA KIT Components
import LocalVerticalResizeBorder from "../../common/components/LocalVerticalResizeBorder";
import FileViewerToolBar from "./FileViewerToolBar";
import FileViewerDockSideBar from "./FileViewerDockSideBar";

//Context Imports
import { withPersistentStorage } from "../../viewer/contexts/PersistentStorageContext";
import { withTiles } from "../../viewer/contexts/TilesContext";
import { FileViewerContext } from "../FileViewer";

const useStyles = makeStyles(() => ({
  sideBarRoot: {
    height: "100%",
    display: "grid",
    overflow: "hidden",
    position: "relative",
    background: "#EBEBEB",
    gridTemplateColumns: "auto 5px auto 5px auto",
  },
}));

function FileViewerSideBar(props) {
  const FV = React.useContext(FileViewerContext);
  const classes = useStyles();

  const triggerZoomAction = (action, n) => {
    if (!FV.rendererDict[FV.activeFileId]) {
      console.warn("No renderer found for the active file ID.");
      return;
    }
    let fileIds = FV.chainListFileIds;
    if (!fileIds.includes(FV.activeFileId)) {
      fileIds = [FV.activeFileId];
    }
    switch (action) {
      case "zoomFit":
        FV.emitRendererEvent({
          type: "zoomFit",
          fileIds: fileIds,
        });
        break;
      case "oneToN":
        FV.emitRendererEvent({
          type: "zoomOneToN",
          value: n,
          fileIds: fileIds,
        });
        break;
      case "increase":
        FV.emitRendererEvent({
          type: "zoomDelta",
          value: -1,
          fileIds: fileIds,
        });
        break;
      case "decrease":
        FV.emitRendererEvent({
          type: "zoomDelta",
          value: 1,
          fileIds: fileIds,
        });
        break;
      default:
        break;
    }
  };

  const rendererRef = FV.rendererDict[FV.activeFileId] ?? null;

  return (
    <Grid
      container
      className={classes.sideBarRoot}
      style={{
        // Toolbar | resizer | sidebar
        gridTemplateColumns: `auto ${FV.treeWidth <= 0 ? 16 : 5}px ${Math.max(
          0,
          FV.treeWidth
        )}px`,
      }}
    >
      <FileViewerToolBar
        isFileSelected={
          FV.activeFileId !== null && FV.activeFileId !== undefined
        }
        triggerZoomAction={triggerZoomAction}
        updateDimensions={FV.updateDimensions}
        splitscreenCount={FV.splitscreenFileIds.length}
        rendererRef={rendererRef}
      />

      <LocalVerticalResizeBorder
        leftBorder={true}
        targetWidth={FV.treeWidth}
        min={300}
        resizeWidth={(newWidth) => {
          FV.updatePreviewWidth(newWidth);
        }}
      />

      <FileViewerDockSideBar {...props} />
    </Grid>
  );
}

FileViewerSideBar.propTypes = {
  persistentStorage: PropTypes.object,
  tiles: PropTypes.object,
};

export default withPersistentStorage(withTiles(FileViewerSideBar));
